import { API_KEY_DOCS } from "./apiKeyDocs";
import { downloadZip } from "../utils/zipWriter";

const normalizeHost = (value) => String(value || "").replace(/^https?:\/\//, "").replace(/\/.*$/, "").trim();

const slugify = (value, fallback = "partner") => {
  const slug = String(value || "")
    .normalize("NFKD")
    .replace(/[^\w.-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase();
  return slug || fallback;
};

const pascalize = (value, fallback = "Partner") => {
  const source = slugify(value, fallback).split(/[-_.]+/).filter(Boolean);
  const pascal = source.map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join("");
  return pascal || fallback;
};

export const buildOemApiKeyBrand = ({ oemBranding, user }) => {
  const host = normalizeHost(oemBranding?.domain || window.location.hostname || "partner.sales-support.jp");
  const displayName =
    oemBranding?.display_name ||
    oemBranding?.name ||
    user?.oem_display_name ||
    user?.brand_name ||
    "Partner Portal";
  const slug = slugify(host.split(".")[0] || displayName, "partner");
  const workspaceRoot = `${pascalize(slug, "Partner")}AgentWorkspace`;

  return {
    productName: displayName,
    apiProductName: `${displayName} API`,
    operatorProductName: `${displayName} Operator API`,
    workspaceDisplayName: `${displayName} Agent Workspace`,
    workspaceRoot,
    workspaceName: `${workspaceRoot}PoweredBySalesBreaker`,
    apiHost: host,
    apiBaseUrl: `https://${host}`,
    apiKeyPlaceholder: `<${slug}_api_key>`,
    apiKeyPagePath: "/app/oem/api-keys",
    zipFilename: `${slug}-agent-workspace.zip`,
    slug,
  };
};

const applyBranding = (text, brand) => {
  let output = String(text || "");
  const replacements = [
    [/JapanGtmAgentWorkspacePoweredBySalesBreaker/g, brand.workspaceName],
    [/JAPAN GTM Agent Workspace powered by SalesBreaker/g, brand.workspaceDisplayName],
    [/JAPAN GTM Agent Workspace/g, brand.workspaceDisplayName],
    [/SalesBreaker SaaS Operator API/g, brand.operatorProductName],
    [/SalesBreaker API/g, brand.apiProductName],
    [/SalesBreaker/g, brand.productName],
    [/salesbreaker\.jp/g, brand.apiHost],
    [/<salesbreaker_api_key>/g, brand.apiKeyPlaceholder],
    [/salesbreaker_api_key/g, brand.apiKeyPlaceholder.replace(/[<>]/g, "")],
    [/salesbreaker-agent-workspace\.zip/g, brand.zipFilename],
    [/\/app\/api-keys/g, brand.apiKeyPagePath],
  ];
  replacements.forEach(([pattern, replacement]) => {
    output = output.replace(pattern, replacement);
  });
  return output;
};

export const buildOemApiKeyDocs = (brand) =>
  API_KEY_DOCS.map((doc) => ({
    ...doc,
    title: applyBranding(doc.title, brand),
    summary: applyBranding(doc.summary, brand),
    filename: doc.filename.replace(/salesbreaker/gi, brand.slug),
    content: applyBranding(doc.content, brand),
  }));

const docById = (docs, id) => docs.find((doc) => doc.id === id)?.content || "";

export const buildOemWorkspaceFiles = (brand, docs = buildOemApiKeyDocs(brand)) => {
  const root = brand.workspaceRoot;
  const readme = `# ${brand.workspaceDisplayName}

This workspace is the local operating pack for Codex, Claude Code, Cursor, ChatGPT Agent, and trusted AI agents working with ${brand.productName}.

Start with:

1. AGENTS.md
2. START_HERE.md
3. workspace-version.json

API base URL: ${brand.apiBaseUrl}
API key page: ${brand.apiKeyPagePath}

Read next:

1. docs/${docs.find((doc) => doc.id === "agent-operating-manual")?.filename || "agent-operating-manual.md"}
2. docs/${docs.find((doc) => doc.id === "agent-contract-pack")?.filename || "agent-contract-pack.md"}
`;

  const rootDocs = docs
    .filter((doc) => !["agents-md", "start-here", "workspace-version"].includes(doc.id))
    .map((doc) => ({
      path: `${root}/docs/${doc.filename}`,
      content: doc.content,
    }));

  return [
    { path: `${root}/AGENTS.md`, content: docById(docs, "agents-md") },
    { path: `${root}/START_HERE.md`, content: docById(docs, "start-here") },
    { path: `${root}/workspace-version.json`, content: docById(docs, "workspace-version") },
    { path: `${root}/README.md`, content: readme },
    ...rootDocs,
    { path: `${root}/logs/README.md`, content: "# logs\n\nKeep temporary operation logs here. Do not store raw API keys.\n" },
    { path: `${root}/templates/README.md`, content: "# templates\n\nKeep generated draft templates and review notes here.\n" },
    { path: `${root}/campaigns/README.md`, content: "# campaigns\n\nKeep campaign planning, preview notes, and approval checkpoints here.\n" },
    { path: `${root}/saved-lists/README.md`, content: "# saved-lists\n\nKeep saved-list review notes and handoff artifacts here.\n" },
    { path: `${root}/analytics/README.md`, content: "# analytics\n\nKeep diagnosis notes, engagement analysis, and next-action summaries here.\n" },
    { path: `${root}/reports/README.md`, content: "# reports\n\nKeep final user-facing reports here.\n" },
    { path: `${root}/strategy/README.md`, content: "# strategy\n\nKeep targeting, messaging, and follow-up strategy notes here.\n" },
  ];
};

export const downloadOemAgentWorkspaceZip = (brand, docs) => {
  downloadZip(brand.zipFilename, buildOemWorkspaceFiles(brand, docs));
};
